import React, { useEffect, useState, useCallback } from 'react';
import { Toolbar, Item as ToolbarItem } from 'devextreme-react/toolbar';
import Button from 'devextreme-react/button';
import TextBox from 'devextreme-react/text-box';
import SelectBox from 'devextreme-react/select-box';
import ScrollView from 'devextreme-react/scroll-view';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';

import {
  CompanySettings,
  getCompanySettings,
  updateCompanySettings,
  configureAccountingProvider,
  removeAccountingProvider,
} from '../../api/company-settings';
import { useAuth } from '../../contexts/auth';
import './company-settings.scss';

const providerOptions = [
  { value: 'None', text: 'Geen' },
  { value: 'ExactOnline', text: 'Exact Online' },
];

export const CompanySettingsPage = () => {
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Company info
  const [companyName, setCompanyName] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  // Email settings
  const [emailFromAddress, setEmailFromAddress] = useState('');
  const [emailFromName, setEmailFromName] = useState('');

  // Accounting provider
  const [providerType, setProviderType] = useState('None');
  const [exactClientId, setExactClientId] = useState('');
  const [exactClientSecret, setExactClientSecret] = useState('');
  const [exactCallbackUrl, setExactCallbackUrl] = useState('');
  const [hasExactConnection, setHasExactConnection] = useState(false);

  const loadSettings = useCallback(async() => {
    setIsLoading(true);
    try {
      const data: CompanySettings = await getCompanySettings(user?.token);
      setCompanyName(data.CompanyName || '');
      setContactEmail(data.ContactEmail || '');
      setEmailFromAddress(data.EmailFromAddress || '');
      setEmailFromName(data.EmailFromName || '');
      setProviderType(data.AccountingProviderType || 'None');
      setExactClientId(data.ExactOnlineClientId || '');
      setExactCallbackUrl(data.ExactOnlineCallbackUrl || '');
      setHasExactConnection(data.HasExactOnlineConnection);
    } catch {
      notify('Fout bij laden van instellingen.', 'error', 3000);
    } finally {
      setIsLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveCompanyInfo = useCallback(async() => {
    setIsSaving(true);
    try {
      await updateCompanySettings(
        {
          CompanyName: companyName,
          ContactEmail: contactEmail,
          EmailFromAddress: emailFromAddress,
          EmailFromName: emailFromName,
        },
        user?.token
      );
      notify('Instellingen opgeslagen.', 'success', 3000);
    } catch {
      notify('Fout bij opslaan van instellingen.', 'error', 3000);
    } finally {
      setIsSaving(false);
    }
  }, [companyName, contactEmail, emailFromAddress, emailFromName, user?.token]);

  const saveAccountingProvider = useCallback(async() => {
    setIsSaving(true);
    try {
      const result = await configureAccountingProvider(
        {
          ProviderType: providerType,
          ExactOnlineClientId: exactClientId,
          ExactOnlineClientSecret: exactClientSecret,
          ExactOnlineCallbackUrl: exactCallbackUrl || undefined,
        },
        user?.token
      );
      notify(result.Message || 'Boekhoudkoppeling opgeslagen.', 'success', 3000);
      if (result.NextStep) {
        notify(result.NextStep, 'info', 6000);
      }
      await loadSettings();
    } catch {
      notify('Fout bij opslaan van boekhoudkoppeling.', 'error', 3000);
    } finally {
      setIsSaving(false);
    }
  }, [providerType, exactClientId, exactClientSecret, exactCallbackUrl, user?.token, loadSettings]);

  const handleRemoveProvider = useCallback(async() => {
    const confirmed = await confirm(
      'Weet u zeker dat u de boekhoudkoppeling wilt verwijderen? Dit verwijdert alle opgeslagen credentials.',
      'Koppeling verwijderen'
    );
    if (!confirmed) return;

    setIsSaving(true);
    try {
      await removeAccountingProvider(user?.token);
      notify('Boekhoudkoppeling verwijderd.', 'success', 3000);
      setProviderType('None');
      setExactClientId('');
      setExactClientSecret('');
      setExactCallbackUrl('');
      setHasExactConnection(false);
    } catch {
      notify('Fout bij verwijderen van koppeling.', 'error', 3000);
    } finally {
      setIsSaving(false);
    }
  }, [user?.token]);

  return (
    <ScrollView className='view-wrapper-scroll'>
      <div className='view-wrapper view-wrapper-company-settings'>
        <Toolbar className='toolbar-details'>
          <ToolbarItem location='before'>
            <span className='toolbar-title'>Bedrijfsinstellingen</span>
          </ToolbarItem>
          <ToolbarItem location='after'>
            <Button
              text='Vernieuwen'
              icon='refresh'
              stylingMode='text'
              onClick={loadSettings}
            />
          </ToolbarItem>
        </Toolbar>

        {isLoading ? (
          <div className='loading-placeholder'>Laden...</div>
        ) : (
          <div className='panels'>
            {/* ── Bedrijfsgegevens ── */}
            <div className='dx-card info-card'>
              <div className='card-header'>
                <i className='dx-icon dx-icon-home card-header-icon' />
                <span>Bedrijfsgegevens</span>
              </div>
              <div className='card-body-form'>
                <div className='form-row'>
                  <label className='form-label'>Bedrijfsnaam</label>
                  <TextBox
                    value={companyName}
                    onValueChanged={(e) => setCompanyName(e.value)}
                    placeholder='Naam van het bedrijf'
                    stylingMode='outlined'
                  />
                </div>
                <div className='form-row'>
                  <label className='form-label'>Contact E-mail</label>
                  <TextBox
                    value={contactEmail}
                    onValueChanged={(e) => setContactEmail(e.value)}
                    placeholder='info@bedrijf.nl'
                    stylingMode='outlined'
                  />
                </div>
              </div>
            </div>

            {/* ── E-mail Instellingen ── */}
            <div className='dx-card info-card'>
              <div className='card-header'>
                <i className='dx-icon dx-icon-email card-header-icon' />
                <span>E-mail Instellingen</span>
              </div>
              <div className='card-body-form'>
                <div className='form-row'>
                  <label className='form-label'>Afzender E-mail</label>
                  <TextBox
                    value={emailFromAddress}
                    onValueChanged={(e) => setEmailFromAddress(e.value)}
                    placeholder='noreply@bedrijf.nl'
                    stylingMode='outlined'
                  />
                </div>
                <div className='form-row'>
                  <label className='form-label'>Afzender Naam</label>
                  <TextBox
                    value={emailFromName}
                    onValueChanged={(e) => setEmailFromName(e.value)}
                    placeholder='Bedrijfsnaam'
                    stylingMode='outlined'
                  />
                </div>
                <div className='form-row hint-row'>
                  <span className='hint-text'>
                    Als deze velden leeg zijn, wordt de standaard afzender gebruikt.
                  </span>
                </div>
              </div>
              <div className='card-footer'>
                <Button
                  text='Opslaan'
                  icon='save'
                  type='default'
                  stylingMode='contained'
                  disabled={isSaving}
                  onClick={saveCompanyInfo}
                />
              </div>
            </div>

            {/* ── Boekhouding Integratie ── */}
            <div className='dx-card info-card'>
              <div className='card-header'>
                <i className='dx-icon dx-icon-money card-header-icon' />
                <span>Boekhouding Integratie</span>
                {hasExactConnection && (
                  <span className='connection-badge connected'>Verbonden</span>
                )}
                {providerType === 'ExactOnline' && !hasExactConnection && (
                  <span className='connection-badge not-connected'>Niet verbonden</span>
                )}
              </div>
              <div className='card-body-form'>
                <div className='form-row'>
                  <label className='form-label'>Provider</label>
                  <SelectBox
                    items={providerOptions}
                    displayExpr='text'
                    valueExpr='value'
                    value={providerType}
                    onValueChanged={(e) => setProviderType(e.value)}
                    stylingMode='outlined'
                  />
                </div>

                {providerType === 'ExactOnline' && (
                  <>
                    <div className='form-row'>
                      <label className='form-label'>Client ID</label>
                      <TextBox
                        value={exactClientId}
                        onValueChanged={(e) => setExactClientId(e.value)}
                        placeholder='Exact Online Client ID'
                        stylingMode='outlined'
                      />
                    </div>
                    <div className='form-row'>
                      <label className='form-label'>Client Secret</label>
                      <TextBox
                        value={exactClientSecret}
                        onValueChanged={(e) => setExactClientSecret(e.value)}
                        placeholder='Exact Online Client Secret'
                        mode='password'
                        stylingMode='outlined'
                      />
                    </div>
                    <div className='form-row'>
                      <label className='form-label'>Callback URL</label>
                      <TextBox
                        value={exactCallbackUrl}
                        onValueChanged={(e) => setExactCallbackUrl(e.value)}
                        placeholder='Wordt automatisch gegenereerd'
                        stylingMode='outlined'
                      />
                    </div>
                  </>
                )}
              </div>
              <div className='card-footer'>
                <Button
                  text='Opslaan'
                  icon='save'
                  type='default'
                  stylingMode='contained'
                  disabled={isSaving}
                  onClick={saveAccountingProvider}
                />
                {providerType !== 'None' && (
                  <Button
                    text='Koppeling verwijderen'
                    icon='trash'
                    type='danger'
                    stylingMode='outlined'
                    disabled={isSaving}
                    onClick={handleRemoveProvider}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </ScrollView>
  );
};
